import { useLayoutEffect, useRef, useState, useSyncExternalStore } from 'react';
import { X, Plus, CheckSquare, Square } from '../../icons';
import { TYPE_COLOR_MAP, type CalendarEventDto, type CalendarEventType } from '../../api/calendar';
import { useEscapeClose } from '../../hooks/useEscapeClose';
import { tCalendar, getCalendarLang, subscribeCalendarLang } from '../../lib/calendar-i18n';
import { formatTime } from '../../lib/base-i18n';
import { TypeIcon } from './calendarTypeMeta';
import { EventCreatorAvatar } from './EventCreatorAvatar';
import { dayWithWeekdayLabel } from './lib/dateLabel';

interface DayPopoverProps {
  date: Date;
  events: CalendarEventDto[];     // 그 날짜의 항목(이미 필터됨).
  anchor: DOMRect | null;         // 클릭한 셀의 위치(앵커드 포지셔닝).
  onClose: () => void;
  onOpenEvent: (e: CalendarEventDto) => void;
  onCreate: (type: CalendarEventType) => void;
  onToggleDone: (e: CalendarEventDto) => void;
}

/**
 * DayPopover (캘린더 상세화 2026-06-07) — 월뷰 날짜 셀 클릭 시 뜨는 레이어 팝업.
 *
 * 모달이 아니라 셀 위치 기준 앵커드 팝오버. 그 날의 항목 목록(할일은 완료 체크박스 인라인) +
 * 기능 메뉴(일정/할일/기념일 추가). 바깥 클릭/Esc 로 닫힘. 뷰포트 밖으로 나가지 않게 clamp.
 */
export function DayPopover({ date, events, anchor, onClose, onOpenEvent, onCreate, onToggleDone }: DayPopoverProps) {
  useSyncExternalStore(subscribeCalendarLang, getCalendarLang, getCalendarLang);
  useEscapeClose(onClose, false);
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  // v2.11.5 — 진입 모션 방향: 앵커 아래로 열림(down) / 위로 플립(up). transform-origin 결정.
  const [placement, setPlacement] = useState<'down' | 'up'>('down');

  // 앵커(셀) 기준 위치 산출 + 뷰포트 clamp.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const w = el.offsetWidth;
    const h = el.offsetHeight;
    const margin = 8;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let top = (anchor?.bottom ?? vh / 2) + 4;
    let left = (anchor?.left ?? vw / 2);
    let dir: 'down' | 'up' = 'down';
    if (left + w + margin > vw) left = vw - w - margin;
    if (left < margin) left = margin;
    if (top + h + margin > vh) {
      top = Math.max(margin, (anchor?.top ?? vh / 2) - h - 4);
      dir = 'up';
    }
    if (top < margin) top = margin;
    setPos({ top, left });
    setPlacement(dir);
  }, [anchor, events.length]);

  /*
    2026-09-28 — 「월」·「일」과 요일 배열(['일','월',…])이 «글자로» 박혀 있어 en·ja 에서도
      한국어가 나왔다. ko 글자는 괄호까지 그대로이고(재서 확인: 「9월 28일 (월)」)
      locale 만 따라간다 — `lib/dateLabel.ts` 참조.
  */
  const title = dayWithWeekdayLabel(date);
  // 종일(휴가/기념일/종일)을 먼저, 그다음 시간순 — 항목 구분이 한눈에.
  const sorted = [...events].sort((a, b) => {
    if (Boolean(a.isAllDay) !== Boolean(b.isAllDay)) return a.isAllDay ? -1 : 1;
    return new Date(a.startAt).getTime() - new Date(b.startAt).getTime();
  });

  const menu: Array<{ type: CalendarEventType; label: string }> = [
    { type: 'meeting', label: tCalendar('calendar.popover.add_event') },
    { type: 'todo', label: tCalendar('calendar.popover.add_todo') },
    { type: 'anniversary', label: tCalendar('calendar.popover.add_anniversary') },
    { type: 'vacation', label: tCalendar('calendar.popover.add_vacation') },
  ];

  return (
    <div className="calendar-daypop-layer" role="presentation" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div
        ref={ref}
        className={`calendar-daypop calendar-daypop--enter calendar-daypop--${placement}`}
        role="dialog"
        aria-modal="false"
        aria-label={title}
        style={{ top: pos.top, left: pos.left }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <header className="calendar-daypop-head">
          <span className="calendar-daypop-title">{title}</span>
          {sorted.length > 0 && <span className="calendar-daypop-count">{sorted.length}</span>}
          <button type="button" className="calendar-daypop-close" onClick={onClose} aria-label={tCalendar('calendar.popover.close')}>
            <X size={16} aria-hidden />
          </button>
        </header>

        {/* 이 날의 항목 — 카테고리 색 보더 + 유형 칩으로 구분 */}
        <div className="calendar-daypop-section">
          <div className="calendar-daypop-section-label">{tCalendar('calendar.popover.items_heading')}</div>
          {sorted.length === 0 ? (
            <div className="calendar-daypop-empty">{tCalendar('calendar.popover.empty')}</div>
          ) : (
            <div className="calendar-daypop-items">
              {sorted.map((e) => {
                const meta = TYPE_COLOR_MAP[e.type as CalendarEventType] ?? { label: '기타', color: 'var(--text-muted)' };
                const isTodo = e.type === 'todo';
                return (
                  <div
                    key={`${e.id}-${e.occurrenceAt ?? ''}`}
                    className={`calendar-daypop-item${isTodo && e.done ? ' calendar-daypop-item--done' : ''}`}
                    style={{ borderLeftColor: meta.color }}
                  >
                    {isTodo ? (
                      <button
                        type="button"
                        className="calendar-daypop-check"
                        onClick={() => onToggleDone(e)}
                        aria-label={tCalendar('calendar.todo.toggle_done')}
                        aria-pressed={Boolean(e.done)}
                      >
                        {e.done ? <CheckSquare size={16} aria-hidden /> : <Square size={16} aria-hidden />}
                      </button>
                    ) : (
                      <span className="calendar-daypop-typeico" style={{ color: meta.color }} aria-hidden>
                        <TypeIcon type={e.type} size={15} />
                      </span>
                    )}
                    <button type="button" className="calendar-daypop-item-main" onClick={() => onOpenEvent(e)}>
                      <span className="calendar-daypop-item-row">
                        <span className="calendar-daypop-time">{e.isAllDay ? tCalendar('calendar.popover.allday') : formatTime(e.startAt)}</span>
                        <EventCreatorAvatar event={e} size="xs" className="calendar-event-avatar" />
                        <span className="calendar-daypop-item-title">{e.title}</span>
                      </span>
                      <span className="calendar-daypop-typechip" style={{ color: meta.color, borderColor: meta.color }}>{meta.label}</span>
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* 추가 — 유형별 기능 메뉴(색·아이콘·라벨 명확) */}
        <div className="calendar-daypop-section calendar-daypop-actions">
          <div className="calendar-daypop-section-label">{tCalendar('calendar.popover.add_label')}</div>
          {menu.map((m) => {
            const meta = TYPE_COLOR_MAP[m.type as CalendarEventType] ?? { label: '', color: 'var(--text-muted)' };
            return (
              <button key={m.type} type="button" className="calendar-daypop-menu-btn" onClick={() => onCreate(m.type)}>
                <span className="calendar-daypop-menu-ico" style={{ color: meta.color }} aria-hidden><TypeIcon type={m.type} size={16} /></span>
                <span className="calendar-daypop-menu-label">{m.label}</span>
                <Plus size={14} aria-hidden className="calendar-daypop-menu-plus" />
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
